import React, { useCallback, useMemo, useState } from 'react';
import WikiIcon from '../../shared/WikiIcon';
import { WikiError, WikiLoading } from '../../shared/WikiStates';
import { wikiUi } from '../../shared/wikiUi';
import BrainTreeCanvas from './BrainTreeCanvas';
import BrainNodeDetail from './BrainNodeDetail';
import BrainNewNodeModal from './BrainNewNodeModal';
import { BRAIN_LEGEND, BRAIN_ROLE_CARDS, BRAIN_TOASTS } from './brainCopy';
import { brainBranchMeta, brainNumber, buildBrainTreeModel, isGroupNode } from './brainTreeModel';
import { brainTreeState, useBrainTreeState } from './brainTreeState';
import { brainErrorText, useBrainNodeActions, useBrainTreeQuery } from './useBrainTree';

function RoleCards() {
    return (
        <div className="wk-brain-roles" data-testid="brain-role-cards">
            {BRAIN_ROLE_CARDS.map((card) => (
                <div key={card.id} className={`wk-card wk-brain-role wk-brain-role--${card.id}`}>
                    <div className="wk-brain-role-head">
                        <span className={`wk-brain-role-chip wk-brain-role-chip--${card.id}`}>{card.chip}</span>
                        <span className="wk-t-name">{card.title}</span>
                    </div>
                    <p className="wk-t-meta wk-brain-role-body">{card.body}</p>
                </div>
            ))}
        </div>
    );
}

function Legend() {
    return (
        <div className="wk-brain-legend" data-testid="brain-legend">
            <span className="wk-brain-legend-item"><span className="wk-brain-legend-dot wk-brain-dot--good" />{BRAIN_LEGEND.good}</span>
            <span className="wk-brain-legend-item"><span className="wk-brain-legend-dot wk-brain-dot--mid" />{BRAIN_LEGEND.mid}</span>
            <span className="wk-brain-legend-item"><span className="wk-brain-legend-dot wk-brain-dot--bad" />{BRAIN_LEGEND.bad}</span>
            <span className="wk-brain-legend-item"><span className="wk-brain-proposed-swatch" />{BRAIN_LEGEND.proposed}</span>
            <span className="wk-brain-legend-item wk-brain-legend-item--icon">
                <WikiIcon name="sparkle" size={11} className="wk-brain-ic-learned" />{BRAIN_LEGEND.learned}
            </span>
            <span className="wk-brain-legend-item wk-brain-legend-item--icon">
                <WikiIcon name="lock" size={11} />{BRAIN_LEGEND.fixed}
            </span>
        </div>
    );
}

/**
 * Criteria tree tab (E1 §1.3): role cards, BRANCHES row (+ New branch / Add a
 * group for managers), stats + legend, the tree canvas and the detail panel,
 * and the new-node modal. Every write goes through the per-node API
 * (contracts/BE-brain.md §3) and toasts the prototype copy.
 *
 * Props (BrainPage, notes/FE-brain-tree.md §1): `canManage`, `readOnly`,
 * `onOpenTab(tabId)`; the others are ignored here.
 */
export default function BrainTreeTab({ canManage = false, onOpenTab }) {
    const treeQuery = useBrainTreeQuery();
    const branchId = useBrainTreeState((s) => s.branchId);
    const selectedId = useBrainTreeState((s) => s.nodeId);
    const [newNode, setNewNode] = useState(null); // { mode, anchorId, seq }
    const actions = useBrainNodeActions();
    const model = useMemo(() => buildBrainTreeModel(treeQuery.data, branchId), [treeQuery.data, branchId]);

    const manage = Boolean(canManage);
    const selected = selectedId && model.byId[selectedId]
        && model.branchNodes.some((n) => n.id === selectedId) ? model.byId[selectedId] : null;
    const testingId = actions.replay.isPending ? actions.replay.variables : null;
    const busy = actions.replay.isPending || actions.activate.isPending || actions.deactivate.isPending
        || actions.flagWeak.isPending || actions.remove.isPending;

    const openNew = useCallback((mode, anchorId) => {
        setNewNode((prev) => ({ mode, anchorId: anchorId || 'root', seq: (prev?.seq || 0) + 1 }));
    }, []);
    const closeNew = useCallback(() => setNewNode(null), []);
    const openReview = useCallback(() => { if (onOpenTab) onOpenTab('review'); }, [onOpenTab]);

    const run = useCallback(async (mutation, id, toast, after) => {
        try {
            await mutation.mutateAsync(id);
            if (after) after();
            if (toast) wikiUi.toast(toast);
        } catch (err) {
            wikiUi.toast(brainErrorText(err));
        }
    }, []);

    const onCreated = useCallback((kind, response) => {
        setNewNode(null);
        if (kind === 'branch') {
            brainTreeState.selectBranchNode(response?.branch?.id, response?.node?.id);
            wikiUi.toast(BRAIN_TOASTS.branchCreated);
            return;
        }
        if (response?.node?.id) brainTreeState.selectNode(response.node.id);
        wikiUi.toast(kind === 'group' ? BRAIN_TOASTS.groupAdded : BRAIN_TOASTS.checkSaved);
    }, []);

    if (treeQuery.isLoading) {
        return <div className="wk-card"><WikiLoading label="Loading the criteria tree…" /></div>;
    }
    if (treeQuery.isError) {
        return (
            <div className="wk-card">
                <WikiError error={treeQuery.error} onRetry={() => treeQuery.refetch()} title="The criteria tree didn't load." />
            </div>
        );
    }

    const { stats, branches, branch, mode } = model;
    const statTiles = [
        ['Checks', brainNumber(stats.checks)],
        ['Fired today', brainNumber(stats.fired_today)],
        ['Learned', brainNumber(stats.learned)],
        ['Proposed', brainNumber(stats.proposed)],
        ['Weak', brainNumber(stats.weak)],
    ];

    return (
        <div className="wk-brain-tree" data-testid="brain-tree-tab">
            <RoleCards />

            <div className="wk-brain-toolbar">
                <div className="wk-brain-branches" data-testid="brain-branches">
                    <span className="wk-t-label wk-brain-branches-label">BRANCHES</span>
                    {branches.map((b) => {
                        const on = b.id === branch.id;
                        return (
                            <button
                                key={b.id}
                                type="button"
                                className={`wk-brain-branch${on ? ' is-active' : ''}`}
                                aria-pressed={on}
                                data-testid={`brain-branch-${b.id}`}
                                onClick={() => brainTreeState.selectBranch(b.id)}
                            >
                                {b.name}
                                <span className="wk-brain-branch-meta">{brainBranchMeta(b)}</span>
                            </button>
                        );
                    })}
                    {manage && (
                        <>
                            <button
                                type="button"
                                className="wk-brain-branch-btn wk-brain-branch-btn--new"
                                data-testid="brain-new-branch"
                                onClick={() => openNew('branch', 'root')}
                            >
                                <WikiIcon name="plus" size={14} />
                                New branch
                            </button>
                            <button
                                type="button"
                                className="wk-brain-branch-btn"
                                data-testid="brain-add-group"
                                onClick={() => openNew('group', 'root')}
                            >
                                <WikiIcon name="folder-plus" size={14} />
                                Add a group
                            </button>
                        </>
                    )}
                </div>

                <div className="wk-brain-statsrow">
                    <div className="wk-brain-stats" data-testid="brain-stats">
                        {statTiles.map(([label, value]) => (
                            <div key={label} className="wk-brain-stat" data-testid={`brain-stat-${label}`}>
                                <span className="wk-t-stat wk-brain-stat-value">{value}</span>
                                <span className="wk-t-meta">{label}</span>
                            </div>
                        ))}
                    </div>
                    <span className="wk-spacer" />
                    <Legend />
                </div>
            </div>

            {mode.message && (
                <div className="wk-brain-mode" role="status" data-testid="brain-mode-banner">
                    <WikiIcon name="pause" size={15} />
                    <span>{mode.message}</span>
                </div>
            )}

            <div className="wk-brain-split">
                <BrainTreeCanvas
                    model={model}
                    selectedId={selected ? selected.id : null}
                    canManage={manage}
                    testingId={testingId}
                    onSelect={(id) => brainTreeState.selectNode(id)}
                    onAddChild={(id) => openNew('child', id)}
                />
                <BrainNodeDetail
                    node={selected}
                    byId={model.byId}
                    canManage={manage}
                    testing={Boolean(selected) && testingId === selected.id}
                    modePaused={mode.paused}
                    busy={busy}
                    onClose={() => brainTreeState.selectNode(null)}
                    onOpenReview={openReview}
                    onAddChild={(id) => openNew('child', id)}
                    onBranchOut={(id) => openNew('sibling', id)}
                    onRunTest={(node) => run(actions.replay, node.id)}
                    onSwitchOn={(node) => run(
                        actions.activate,
                        node.id,
                        isGroupNode(node) ? BRAIN_TOASTS.groupSwitchedOn : BRAIN_TOASTS.switchedOn,
                    )}
                    onTurnOff={(node) => {
                        if (node.fixed) {
                            wikiUi.toast(BRAIN_TOASTS.fixedCantTurnOff);
                            return;
                        }
                        run(actions.deactivate, node.id, BRAIN_TOASTS.turnedOff);
                    }}
                    onFlag={(node) => run(actions.flagWeak, node.id, BRAIN_TOASTS.flagged)}
                    onDelete={(node) => run(actions.remove, node.id, BRAIN_TOASTS.deleted, () => brainTreeState.selectNode(null))}
                />
            </div>

            {manage && newNode && (
                <BrainNewNodeModal
                    key={newNode.seq}
                    mode={newNode.mode}
                    anchorId={newNode.anchorId}
                    model={model}
                    onCancel={closeNew}
                    onCreated={onCreated}
                />
            )}
        </div>
    );
}
